/**
 * Journey: IVF, With Surrogate, Female
 *
 * Steps: Learn > Test > Clinic > Prep > Treatment > Wait
 *
 * For a woman using her own eggs with a gestational surrogate
 * who carries the pregnancy. The user undergoes stimulation and
 * retrieval; the surrogate receives the embryo transfer.
 */

import type { JourneyDefinition, PlanStep } from "../plan.types";

const learnStep: PlanStep = {
  id: "learn",
  title: "How IVF with a Surrogate Works",
  subtitle: "Your eggs, a surrogate carries",
  icon: "BookOpen",
  education: {
    context:
      "In gestational surrogacy IVF, your eggs are retrieved and fertilized, then the embryo is transferred to your surrogate who carries the pregnancy. The surrogate has no genetic connection to the baby.",
    details:
      "The full process:\n1. Ovarian stimulation (10-14 days): You take injectable medications to mature multiple eggs\n2. Egg retrieval (20 minutes): Your eggs are collected under light sedation\n3. Fertilization (day 0): Eggs and sperm are combined in the lab\n4. Embryo culture (5-7 days): Embryos develop to blastocyst stage\n5. Embryo transfer (5 minutes): A single embryo is transferred to your surrogate's uterus\n6. Two-week wait: Your surrogate waits ~14 days for a blood pregnancy test (beta hCG)\n\nKey difference from standard IVF: you undergo the egg retrieval, but your surrogate carries the pregnancy. This requires coordination between your cycle and your surrogate's preparation.",
    learnMoreLink: "ivf-guide",
  },
  actions: [
    {
      label: "I understand this",
      variant: "primary",
      icon: "Check",
      signal: "complete-step",
    },
    {
      label: "Full IVF guide",
      variant: "outline",
      icon: "BookOpen",
      screen: "LearnScreen",
      params: { article: "ivf-guide" },
    },
  ],
  completionSignal: { type: "manual" },
  conditionModifiers: [
    {
      condition: "pcos",
      additionalEducation:
        "You mentioned PMOS (ex PCOS). This affects your stimulation protocol: you may respond very strongly (risk of OHSS), your clinic will likely use lower medication doses and monitor more frequently. You may produce more eggs, but quality can vary. PGT-A testing helps select the best embryo.",
    },
    {
      condition: "endometriosis",
      additionalEducation:
        "You mentioned endometriosis. Endometriosis can affect egg quality, especially with ovarian endometriomas. Your clinic may recommend a longer suppression phase before stimulation.",
    },
    {
      condition: "age-38-plus",
      additionalEducation:
        "At your age, your clinic may recommend PGT-A to screen embryos for chromosomal abnormalities, more aggressive stimulation protocols, and banking embryos across multiple cycles before transferring to your surrogate.",
    },
  ],
  ageSpecificContent: [
    {
      range: [0, 34],
      content:
        "Under 35, egg quality is generally strong. Success rates with surrogacy are high when embryos are PGT-A tested.",
    },
    {
      range: [35, 37],
      content:
        "At 35-37, PGT-A testing can help select the best embryo for transfer to your surrogate.",
    },
    {
      range: [38, 40],
      content:
        "At 38-40, multiple retrieval cycles may be needed to bank enough quality embryos. Your surrogate's uterine environment is a separate positive factor.",
    },
    {
      range: [41, 99],
      content:
        "Over 40, egg quality declines significantly. Your RE may discuss using donor eggs. The surrogate's age does not affect egg quality.",
    },
  ],
};

const testStep: PlanStep = {
  id: "test",
  title: "Baseline Fertility Testing",
  subtitle: "Your egg reserve and health screening",
  icon: "TestTube",
  education: {
    context:
      "Your testing focuses on ovarian reserve to plan the stimulation protocol. Your surrogate undergoes separate medical screening through the surrogacy agency or clinic to confirm she's ready to carry.",
  },
  actions: [
    {
      label: "Order at-home AMH test",
      variant: "primary",
      screen: "AtHomeFertilityTesting",
    },
    {
      label: "Upload test results",
      variant: "outline",
      screen: "DocumentsScreen",
    },
  ],
  substeps: [
    {
      id: "amh",
      title: "AMH (Anti-Müllerian Hormone)",
      education:
        "Measures your egg supply. The single most important test for protocol planning.",
      status: "not-started",
    },
    {
      id: "fsh-estradiol",
      title: "FSH + Estradiol",
      education: "Cycle day 2-3 blood test. Shows ovarian function.",
      status: "not-started",
    },
    {
      id: "afc",
      title: "Antral Follicle Count (AFC)",
      education:
        "Transvaginal ultrasound. Combined with AMH, predicts stimulation response.",
      status: "not-started",
    },
    {
      id: "infectious-disease",
      title: "Infectious disease screening",
      education:
        "Required before any IVF cycle. Standard panel: HIV, Hepatitis B/C, syphilis, gonorrhea, chlamydia.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "all-substeps" },
};

const clinicStep: PlanStep = {
  id: "clinic",
  title: "Find Your Clinic",
  subtitle: "Choose a clinic experienced with surrogacy",
  icon: "Building2",
  education: {
    context:
      "For surrogacy IVF, look for clinics experienced in third-party reproduction. Check SART.org for clinic-specific data. Key metrics: live birth rate per transfer. Also consider whether the clinic coordinates with surrogacy agencies or if you need to arrange that separately.",
  },
  actions: [
    {
      label: "Browse clinics near you",
      variant: "primary",
      screen: "FindCareScreen",
      params: { filter: "ivf" },
    },
    {
      label: "Compare clinic costs",
      variant: "outline",
      screen: "CostsOverviewScreen",
    },
  ],
  substeps: [
    {
      id: "research-clinics",
      title: "Research clinics",
      education:
        "Compare SART success rates. Ask about their surrogacy coordination experience.",
      status: "not-started",
    },
    {
      id: "schedule-consultation",
      title: "Schedule a consultation",
      education:
        "30-60 minute initial consultation to review your case and surrogacy plan.",
      status: "not-started",
    },
    {
      id: "choose-clinic",
      title: "Choose your clinic",
      education: "Your clinic will start your onboarding process.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "manual" },
};

const prepStep: PlanStep = {
  id: "prep",
  title: "Get Ready for Your Cycle",
  subtitle: "Surrogate matching, legal, and medical prep",
  icon: "ClipboardCheck",
  education: {
    context:
      "Surrogacy IVF prep involves matching with a surrogate, legal agreements, medical history, insurance, and decisions about genetic testing.",
  },
  actions: [
    {
      label: "Continue medical history",
      variant: "primary",
      screen: "MedicalHistory",
    },
    {
      label: "Upload insurance card",
      variant: "secondary",
      screen: "CoverageFlow",
    },
  ],
  substeps: [
    {
      id: "match-surrogate",
      title: "Match with a surrogate",
      education:
        "Work with a surrogacy agency or your clinic to find a gestational carrier. The matching process typically takes 1-3 months and includes medical and psychological screening.",
      status: "not-started",
    },
    {
      id: "legal-agreements",
      title: "Legal agreements",
      education:
        "Both parties need independent legal counsel. Surrogacy contracts cover compensation, medical decisions, parental rights, and expectations. This must be completed before any medical procedures begin.",
      status: "not-started",
    },
    {
      id: "surrogate-clearance",
      title: "Surrogate medical clearance",
      education:
        "Your surrogate undergoes a full medical evaluation including uterine assessment, infectious disease screening, and psychological evaluation.",
      status: "not-started",
    },
    {
      id: "medical-history",
      title: "Complete your medical history",
      education: "Identifies factors affecting your stimulation protocol.",
      status: "not-started",
    },
    {
      id: "insurance-card",
      title: "Upload insurance card",
      education: "Needed for billing. Surrogate may have separate insurance.",
      status: "not-started",
    },
    {
      id: "pgta-decision",
      title: "PGT-A decision",
      education:
        "Decide whether to genetically test embryos before transfer. Cost: $3,000-$5,000. Recommended for surrogacy to maximize the chance of success with each transfer.",
      status: "not-started",
    },
    {
      id: "consent-forms",
      title: "Sign consent forms",
      education:
        "Covers procedure, risks, embryo disposition, and surrogacy-specific consents.",
      status: "not-started",
    },
    {
      id: "supplements",
      title: "Start supplements (optional)",
      education:
        "CoQ10 and vitamin D for egg quality. Start 2-3 months before if possible.",
      status: "not-started",
      actions: [
        {
          label: "See supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      ],
    },
  ],
  completionSignal: {
    type: "completion-percent",
    field: "prepReadiness",
    threshold: 80,
  },
};

const treatmentStep: PlanStep = {
  id: "treatment",
  title: "Your IVF Cycle",
  subtitle: "Active treatment with daily guidance",
  icon: "Syringe",
  education: {
    context:
      "Your IVF cycle is active. Track daily medications, appointments, and symptoms in the Care section.",
    details:
      "Your cycle: baseline → stimulation → trigger → egg retrieval.\n\nThen your surrogate's cycle: preparation → embryo transfer → two-week wait.\n\nYour retrieval and your surrogate's transfer may happen in the same cycle (fresh transfer) or in separate cycles (frozen transfer).\n\nYour Care page has your daily medication schedule, symptom tracker, and appointment calendar.",
  },
  actions: [
    {
      label: "Go to Care",
      variant: "primary",
      icon: "Heart",
      screen: "CareOverview",
    },
    { label: "Set your start date", variant: "outline", icon: "Calendar" },
  ],
  completionSignal: { type: "external" },
};

const waitStep: PlanStep = {
  id: "wait",
  title: "The Two-Week Wait",
  subtitle: "Between transfer and pregnancy test",
  icon: "Clock",
  education: {
    context:
      "The two-week wait (TWW) is the period between embryo transfer to your surrogate and the pregnancy blood test.",
    details:
      "Here's what's happening:\nDays 1-3: The embryo hatches and begins attaching to your surrogate's uterine lining\nDays 4-5: Implantation begins\nDays 6-8: hCG hormone starts being produced if implantation is successful\nDays 9-14: hCG levels rise enough to be detectable by blood test\n\nThis wait is unique because the physical process is happening in someone else's body. You may feel a mix of hope, anxiety, and a sense of limited control. All of that is completely normal.\n\nYour surrogate will follow her clinic's instructions for progesterone and activity. Stay in touch, be supportive, and trust the process. The blood test (beta hCG) is the only reliable answer.",
  },
  actions: [
    {
      label: "Log how you're feeling today",
      variant: "primary",
      icon: "Heart",
    },
    { label: "Support resources", variant: "secondary", icon: "Users" },
    { label: "When is the blood test?", variant: "outline", icon: "Calendar" },
  ],
  completionSignal: { type: "external" },
};

export const IVF_SURROGATE: JourneyDefinition = {
  key: "ivf-with-surrogate",
  gender: "female",
  goal: "pregnancy",
  pathway: "with-surrogate",
  title: "IVF",
  description: "In vitro fertilization with a gestational surrogate",
  steps: [learnStep, testStep, clinicStep, prepStep, treatmentStep, waitStep],
};
